import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { type QueryFunction, useRequest, MutationFunction } from '../common';
import {
  getIntegrationActionDetailsKey,
  getIntegrationActionListKey,
} from './keys';
import { transformManagedIntegrationActionFiltersParamsToApi } from './managedIntegrationUtils';

//Fetches entire list of available integration actions for a managed integration
export const useIntegrationActionList: QueryFunction<
  any[],
  [managedIntegrationUuid: string],
  ReturnType<typeof getIntegrationActionListKey>
> = (managedIntegrationUuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getIntegrationActionListKey(managedIntegrationUuid),
    queryFn: ({ signal }) =>
      request(
        `/managed_integrations/${managedIntegrationUuid}/integration_actions`,
        {
          init: { signal },
          query:
            typeof query === 'string'
              ? query
              : transformManagedIntegrationActionFiltersParamsToApi(
                  query ?? {},
                ),
        },
      ),
  });
};

//This create an integration action, overwrites QueryData for that individual action,
//and invalidates all related action definition list queries
export const useCreateIntegrationAction: MutationFunction<
  unknown,
  { uuid: string; managedIntegrationUuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: ({ managedIntegrationUuid, body }) => {
      return request(
        `/managed_integrations/${managedIntegrationUuid}/integration_actions`,
        {
          query,
          init: { method: 'POST', body },
        },
      );
    },
    ...queryOptions,
    mutationKey: ['createIntegrationAction'],
    onSuccess: (data, requestData, ...rest) => {
      const key = getIntegrationActionDetailsKey(requestData.uuid);
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({
        queryKey: getIntegrationActionListKey(
          requestData.managedIntegrationUuid,
        ),
      });
      queryOptions?.onSuccess?.(data, requestData, ...rest);
    },
  });
};

export default {
  useIntegrationActionList,
  useCreateIntegrationAction,
};
